import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import { FAR_STATE, FAR_LINK_RECIPIENT, FAR_EXCLUSION } from "../../../../assets/js/utils/far/farEngine";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";
import { FAR_STATE_TONE, FarNotice, FarPill } from "./farUi";
import FarRuleDetails from "./FarRuleDetails";
import LoaderComponent from "../../Loader";

const useStyles = makeStyles(theme => {
    const s = farShared(theme);
    const t = s.t;
    return {
        panel: { ...s.card, padding: "18px", textAlign: "left", display: "flex", flexDirection: "column", gap: "14px" },
        head: { display: "flex", gap: "10px", alignItems: "center" },
        shield: { color: t.success.solid, width: "30px !important", height: "30px !important", flexShrink: 0 },
        title: { margin: 0, fontSize: "18px", fontWeight: 700, color: t.textStrong },
        intro: { margin: 0, fontSize: "14px", lineHeight: "20px", color: t.textMuted },
        learn: { ...s.focusRing, background: "none", border: "none", padding: 0, color: t.link, fontWeight: 600, fontSize: "14px", cursor: "pointer", textDecoration: "underline" },
        file: { border: "1px solid "+t.border, borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "10px" },
        fileHead: { display: "flex", alignItems: "center", gap: "8px", justifyContent: "space-between", flexWrap: "wrap" },
        fileName: { margin: 0, fontWeight: 700, fontSize: "15px", color: t.textStrong, wordBreak: "break-all", flex: "1 1 140px" },
        more: { margin: 0, fontSize: "13px", color: t.textMuted }
    };
});

const STATE_LABEL = {
    [FAR_STATE.MATCHED]: "Rule matched",
    [FAR_STATE.AUTHORIZED]: "Rule matched",
    [FAR_STATE.OVERRIDDEN]: "Exception authorized",
    [FAR_STATE.MIXED]: "Some not authorized",
    [FAR_STATE.UNAUTHORIZED]: "Not authorized",
    [FAR_STATE.LINK]: "Not allowed by link",
    [FAR_STATE.EXCLUDED]: "Recipient blocked"
};

function summary(evaluation){
    const bad = evaluation.unauthorizedRecipients.filter(r => r !== FAR_LINK_RECIPIENT);
    switch(evaluation.state){
        case FAR_STATE.EXCLUDED: {
            const excluded = evaluation.excludedRecipients || {};
            const names = Object.keys(excluded);
            const hard = names.filter(r => excluded[r].action === FAR_EXCLUSION.BLOCK || !excluded[r].overridable);
            const others = bad.filter(r => !excluded[r]);
            let text = "Blocked by FAR exclusion: "+names.join(", ")+". ";
            text += hard.length === names.length ? "Remove "+(names.length === 1 ? "this recipient" : "these recipients")+" to send." : (hard.length > 0 ? "Remove "+hard.join(", ")+" to send." : "An authorized exception is required to send.");
            if(others.length > 0){
                text += " Also not authorized: "+others.join(", ")+".";
            }
            return { tone: "danger", text };
        }
        case FAR_STATE.AUTHORIZED:
            return { tone: "success", text: "All recipients are authorized for "+(evaluation.matchedFiles.length > 1 ? "these files." : "this file.") };
        case FAR_STATE.MATCHED:
            return { tone: "info", text: "Add recipients to check them against the matched rules." };
        case FAR_STATE.OVERRIDDEN:
            return { tone: "warning", text: "Exceptions are authorized for "+evaluation.overriddenPairs.length+" file/recipient pair"+(evaluation.overriddenPairs.length === 1 ? "" : "s")+". They will be recorded in the audit log." };
        case FAR_STATE.LINK:
            return { tone: "danger", text: "Matched files are restricted to named recipients. Send them by email, or authorize an exception." };
        case FAR_STATE.MIXED:
        case FAR_STATE.UNAUTHORIZED:
            return { tone: evaluation.state === FAR_STATE.MIXED ? "warning" : "danger", text: bad.length+" recipient"+(bad.length === 1 ? " is" : "s are")+" not authorized: "+bad.join(", ") };
        default:
            return null;
    }
}

function FarCheckPanel({ evaluation, loading, error, onRetry, hasFiles, deferred, onLearnMore }){
    const classes = useStyles();
    const [expanded, setExpanded] = React.useState({});
    const settings = evaluation ? evaluation.settings : {};
    let body = null;

    if(loading){
        body = <LoaderComponent color="custom" align="center" />;
    }else if(error){
        body = (
            <FarNotice tone="warning" action={onRetry ? <button type="button" className={classes.learn} onClick={onRetry}>Retry</button> : null}>
                {error} Your transfer is still checked by the server when you send.
            </FarNotice>
        );
    }else if(!hasFiles){
        body = <FarNotice tone="neutral">Add files to check them against your File Attachment Rules.</FarNotice>;
    }else if(deferred){
        body = <FarNotice tone="neutral">Files and recipients are checked when you press Send.</FarNotice>;
    }else if(!evaluation || evaluation.matchedFiles.length <= 0){
        body = <FarNotice tone="neutral"><strong>No File Attachment Rule applies.</strong> You can send to any recipient.</FarNotice>;
    }else{
        const shown = evaluation.matchedFiles.slice(0, 5);
        const result = summary(evaluation);
        body = (
            <>
                {
                    shown.map(file => (
                        <div className={classes.file} key={file.path}>
                            <div className={classes.fileHead}>
                                <p className={classes.fileName}>{file.name}</p>
                                <FarPill tone={FAR_STATE_TONE[file.state]}>{STATE_LABEL[file.state] || "Rule matched"}</FarPill>
                            </div>
                            <FarRuleDetails rule={file.matches[0].rule} evaluation={file.matches[0].evaluation} settings={settings} file={file} />
                            {
                                file.matches.length > 1 ?
                                    expanded[file.path] ?
                                        file.matches.slice(1).map(match => <FarRuleDetails key={match.rule.id} rule={match.rule} evaluation={match.evaluation} settings={settings} file={file} />)
                                    :
                                        <button type="button" className={classes.learn} style={{alignSelf: "flex-start"}} aria-expanded="false"
                                            onClick={() => setExpanded(Object.assign({}, expanded, { [file.path]: true }))}>
                                            +{file.matches.length - 1} more matching rule{file.matches.length - 1 > 1 ? "s" : ""}
                                        </button>
                                : null
                            }
                        </div>
                    ))
                }
                { evaluation.matchedFiles.length > shown.length ? <p className={classes.more}>+{evaluation.matchedFiles.length - shown.length} more matching files</p> : null }
                { result ? <FarNotice tone={result.tone}>{result.text}</FarNotice> : null }
            </>
        );
    }

    return (
        <section className={classes.panel} aria-labelledby="far-check-title" aria-live="polite">
            <div className={classes.head}>
                <VerifiedUser className={classes.shield} aria-hidden="true" />
                <h2 className={classes.title} id="far-check-title">File Attachment Rules Check</h2>
            </div>
            <p className={classes.intro}>
                The attached file(s) will be validated against your File Attachment Rules before sending.{" "}
                { onLearnMore ? <button type="button" className={classes.learn} onClick={onLearnMore}>Learn more</button> : null }
            </p>
            {body}
        </section>
    );
}

FarCheckPanel.defaultProps = { evaluation: null, loading: false, error: null, onRetry: null, hasFiles: false, deferred: false, onLearnMore: null };
FarCheckPanel.propTypes = {
    evaluation: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRetry: PropTypes.func,
    hasFiles: PropTypes.bool,
    deferred: PropTypes.bool,
    onLearnMore: PropTypes.func
};

export default FarCheckPanel;
